import * as React from 'react';
import { connect } from 'react-redux';

import {
  ModalFrame,
  ModalFrameFooter,
  ModalFrameHeader,
  ModalFrameInner
} from 'components/modal';
import { Form, FormField } from 'components/form';
import ActionButton from 'components/buttons/action-button/ActionButton';
import Input from 'components/inputs/input/Input';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import LightweightProfileModal from 'modules/lightweight/components/LightweightProfileModal';

import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { createBrandChannel } from 'modules/insights/sov/reducers/posts/collabary/sov-posts';

import { isInstagramPostUrl, isRequired } from 'utils/validators';
import { __t, applyTranslations } from 'utils/language';
import { API, post } from 'utils/api';
import { logError } from 'utils/log';
import { sanitizeGenericHtml } from 'utils/sanitize';

import './sov-add-post.scss';

import type { Dispatch } from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./sov-add-post.i18n.json'));

type OwnProps = {
  onBeforeCloseModal: () => void
};

type DispatchProps = {
  dispatchAddBrandChannel: (b: string, a: string) => Promise<void>,
  dispatch: Dispatch
};

type Props = OwnProps & DispatchProps;
type Mode = 'add_post' | 'add_post_success' | 'add_post_success_brand_channel' | 'add_lightweight_profile' | 'add_post_failure';

type State = {
  postUrl: string,
  mode: Mode,
  isValid: boolean,
  username: string,
  requestError: string
};

const INITIAL_STATE: State = {
  postUrl: '',
  username: '',
  mode: 'add_post',
  isValid: true,
  requestError: ''
};

const SOVAddPost: React.FunctionComponent<Props> = (props: Props) => {
  const { onBeforeCloseModal } = props;
  const [state, setState] = React.useState<State>(INITIAL_STATE);

  const handleInputChange = (event: SyntheticEvent<HTMLInputElement>): void => {
    const { currentTarget } = event;
    const value = currentTarget.value;
    const name = currentTarget.name;

    setState((prevState) => ({
      ...prevState,
      [name]: value,
      isValid: true,
      requestError: ''
    }));
  };

  const handleModeChange = (mode: Mode): void => {
    setState((prevState) => ({
      ...prevState,
      mode
    }));
  };

  const addPost = (type?: string): void => {
    const { postUrl } = state;
    const { dispatch } = props;
    const url = API.END_POINTS.SOV.addPost();

    dispatch(showLoading(url));

    post(url, {
      data: {
        postUrl,
        channelType: 'instagram'
      }
    }).then(response => {
      const { isChannelExists, username } = response.data;

      if (!isChannelExists) {
        setState({ ...state, username, mode: 'add_post_failure' });
      } else if (type === 'brand') {
        setState({ ...state, mode: 'add_post_success_brand_channel' });
      } else if (type !== 'influencer') {
        setState({ ...state, mode: 'add_post_success' });
      }
    }).catch(error => {
      const { code } = error.response.data;

      logError(error);

      setState({
        ...state,
        requestError: __t(
          `sov_add_post.errors.${
            typeof code === 'string'
              ? code.split('.').join('_')
              : 'internal_error'
          }`,
          { url: postUrl }
        )
      });
    }).finally(() => {
      dispatch(hideLoading(url));
    });
  };

  const handleProfileCreated = () => {
    addPost('influencer');
  };

  const onValidationErrors = () => {
    setState((prevState) => ({
      ...prevState,
      isValid: false
    }));
  };

  const addBrandChannel = (): void => {
    const { dispatchAddBrandChannel } = props;
    const { username } = state;

    dispatchAddBrandChannel(username, 'instagram')
      .then(() => {
        addPost('brand');
      });
  };

  const renderForm = (): JSX.Element => {
    const { postUrl, requestError } = state;

    return (
      <div className="sov-add-post__post-url-container">
        <FormField
          showRequiredMark
          label={__t('sov_add_post.url_title')}
          labelType="highlighted"
          fontSize="small"
          direction="column"
          showValidationIcon={false}
          validators={[
            isRequired(postUrl),
            isInstagramPostUrl(postUrl)
          ]}
          immediateValidationError={requestError}
        >
          <Input
            type="text"
            autoFocus
            name="postUrl"
            value={postUrl}
            width="responsive"
            onChange={handleInputChange}
            placeholder="instagram.com/p/"
          />
        </FormField>
      </div>
    );
  };

  const renderBody = (): JSX.Element | null => {
    const { mode } = state;

    if (mode === 'add_post') {
      return renderForm();
    }

    if (mode === 'add_post_success') {
      return (
        <div className="sov-add-post__success-message-container">
          {__t('sov_add_post.success_message_disclaimer')}
        </div>
      );
    }

    if (mode === 'add_post_failure') {
      return (
        <div className="sov-add-post__failure-message-container">
          <div>
            <div>
              {__t('sov_add_post.unknown_channel_message')}
            </div>
            <div dangerouslySetInnerHTML={{ __html: sanitizeGenericHtml(__t('sov_add_post.unknown_channel_question')) }} />
          </div>
        </div>
      );
    }

    if (mode === 'add_post_success_brand_channel') {
      return (
        <div className="sov-add-post__brand-channel-success-container">
          <SVGDynamicIcon
            name="profile-custom-bg"
            width={78}
            height={78}
          />
          <div className="sov-add-post__brand-channel-success-message">
            {__t('sov_add_post.brand_channel_success_message')}
          </div>
          <div dangerouslySetInnerHTML={{ __html: sanitizeGenericHtml(__t('sov_add_post.success_message_disclaimer')) }} />
        </div>
      );
    }

    return null;
  };

  const renderFooter = (): JSX.Element | null => {
    const { mode, postUrl } = state;

    if (mode === 'add_post') {
      return (
        <React.Fragment>
          <ActionButton
            color="tertiary"
            onClick={onBeforeCloseModal}
          >
            {__t('sov_add_post.action_button.cancel')}
          </ActionButton>

          <ActionButton
            color="secondary"
            type="submit"
            disabled={!postUrl}
          >
            {__t('sov_add_post.action_button.add_post')}
          </ActionButton>
        </React.Fragment>
      );
    }

    if (mode === 'add_post_success' || mode === 'add_post_success_brand_channel') {
      return (
        <ActionButton
          color="secondary"
          onClick={onBeforeCloseModal}
        >
          {__t('sov_add_post.action_button.acknowledge_btn')}
        </ActionButton>
      );
    }

    if (mode === 'add_post_failure') {
      return (
        <React.Fragment>
          <ActionButton
            color="secondary"
            onClick={() => addBrandChannel()}
          >
            {__t('sov_add_post.action_button.brand_channel_btn')}
          </ActionButton>

          <ActionButton
            color="secondary"
            onClick={() => handleModeChange('add_lightweight_profile')}
          >
            {__t('sov_add_post.action_button.influencer_channel_btn')}
          </ActionButton>
        </React.Fragment>
      );
    }

    return null;
  };

  const { username } = state;

  if (state.mode === 'add_lightweight_profile') {
    return (
      <LightweightProfileModal
        onProfileCreated={handleProfileCreated}
        onBeforeCloseModal={onBeforeCloseModal}
        instagramChannel={username}
        onAddAnotherPost={() => { setState(INITIAL_STATE); }}
      />
    );
  }

  return (
    <ModalFrame>
      <ModalFrameHeader title={__t(`sov_add_post.header_title.${state.mode}`)} />
      <ModalFrameInner>
        <Form
          onSubmit={() => addPost()}
          onValidationErrors={onValidationErrors}
        >
          { renderBody() }
          <ModalFrameFooter noMargin>
            { renderFooter() }
          </ModalFrameFooter>
        </Form>
      </ModalFrameInner>
    </ModalFrame>
  );
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchAddBrandChannel: (username: string, type: string) => dispatch(createBrandChannel(username, type)),
  dispatch
});

export default connect(null, mapDispatchToProps)(SOVAddPost);
